import React, { useState, useEffect } from 'react';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import {
  Card,
  CardContent,
  Checkbox,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Box,
  IconButton
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add'; // Import Add icon
import EditIcon from '@material-ui/icons/Edit'; // Import Edit icon
import { graphqlServiceRequest } from '../../bitComponents/utils';
import { API_URL } from '../../constants/constants';

const useStyles = makeStyles((theme) => ({
  container: {
    maxHeight: 400,
    overflowY: 'auto',
    border: '1px solid #ddd',
    borderRadius: 5
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  deleteButton: {
    marginLeft: theme.spacing(2)
  },
  editButton: {
    marginLeft: theme.spacing(1)
  }
}));

const TruckCabinTypeList = ({ token, ...props }) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [truckCabinTypes, setTruckCabinTypes] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [newCabinTypeName, setNewCabinTypeName] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editCabinTypeId, setEditCabinTypeId] = useState(null);

  const generateAddingQuery = () => {
    return `
      mutation {
        createTruckCabinType(input: {
          truckCabinType: {
            name: "${newCabinTypeName}"
          }
        }) {
          truckCabinType {
            id
          }
        }
      }
    `;
  };

  const generateUpdatingQuery = (id) => {
    return `
      mutation {
        updateTruckCabinTypeById(input: {patch: {name: "${newCabinTypeName}"}, id: ${id}}) {
          clientMutationId
        }
      }
    `;
  };

  const fetchData = async () => {
    setSelectedIds([]);
    setLoading(true);

    try {
      const { data } = await dataProvider.getList('TruckCabinType', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: {}
      });

      setTruckCabinTypes(data || []);
    } catch (error) {
      notify('שגיאה בשליפת סוג קאבינה', { type: 'error' });
    }

    setLoading(false);
  };

  const handleToggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    try {
      setLoading(true);

      await Promise.all(
        selectedIds.map((id) => dataProvider.delete('TruckCabinType', { id }))
      );

      notify('סוגי קאבינה נמחקו בהצלחה', { type: 'success' });
      setSelectedIds([]);
      fetchData();
      setLoading(false);
    } catch (error) {
      setLoading(false);
      notify('שגיאה במחיקת סוגי קאבינה', { type: 'error' });
    }
  };

  const handleAdd = async () => {
    if (!newCabinTypeName.trim() || !token) return;

    // Prevent duplicate names
    if (
      truckCabinTypes.some(
        (item) =>
          item.name.toLowerCase() === newCabinTypeName.trim().toLowerCase()
      )
    ) {
      notify('הסוג קאבינה הזה כבר קיים', { type: 'warning' });
      return;
    }

    try {
      await graphqlServiceRequest(token, generateAddingQuery(), API_URL);

      notify('סוג קאבינה נוסף בהצלחה', { type: 'success' });
      setNewCabinTypeName('');
      setOpenDialog(false);
      fetchData();
    } catch (error) {
      notify('שגיאה בהוספת סוג קאבינה', { type: 'error' });
    }
  };

  const handleUpdate = async () => {
    if (!newCabinTypeName.trim() || !token) return;

    // Prevent duplicate names
    if (
      truckCabinTypes.some(
        (item) =>
          item.name.toLowerCase() === newCabinTypeName.trim().toLowerCase()
      )
    ) {
      notify('הסוג קאבינה הזה כבר קיים', { type: 'warning' });
      return;
    }

    try {
      await graphqlServiceRequest(
        token,
        generateUpdatingQuery(editCabinTypeId),
        API_URL
      );

      notify('סוג קאבינה עודכן בהצלחה', { type: 'success' });
      setNewCabinTypeName('');
      setOpenDialog(false);
      setIsEditMode(false);
      setEditCabinTypeId(null);
      fetchData();
    } catch (error) {
      notify('שגיאה בעדכון סוג קאבינה', { type: 'error' });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Card>
      <CardContent>
        <Box className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={() => setOpenDialog(true)}
            startIcon={<AddIcon />} // Add the plus icon here
          >
            הוסף סוג קאבינה
          </Button>

          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="secondary"
              disabled={loading}
              style={{
                backgroundColor: 'red', // Red background color
                color: 'white', // White text color
                '&:hover': {
                  backgroundColor: 'darkred' // Dark red on hover
                }
              }}
              onClick={handleDelete}
              className={classes.deleteButton}
            >
              מחק ({selectedIds.length})
            </Button>
          )}
        </Box>

        {loading ? (
          <CircularProgress style={{ display: 'block', margin: '20px auto' }} />
        ) : (
          <List className={classes.container}>
            {truckCabinTypes.map((item) => (
              <ListItem
                key={item.id}
                button
                onClick={() => handleToggle(item.id)}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedIds.includes(item.id)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={item.name} />
                <IconButton
                  className={classes.editButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditMode(true);
                    setEditCabinTypeId(item.originalId);
                    setNewCabinTypeName(item.name);
                    setOpenDialog(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {isEditMode ? 'עדכן סוג קאבינה' : 'הוסף סוג קאבינה חדש'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="שם סוג קאבינה"
            fullWidth
            value={newCabinTypeName}
            onChange={(e) => setNewCabinTypeName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setNewCabinTypeName('');
            }}
            color="secondary"
          >
            ביטול
          </Button>
          <Button
            onClick={isEditMode ? handleUpdate : handleAdd}
            color="primary"
          >
            {isEditMode ? 'עדכן' : 'שמור'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TruckCabinTypeList;
